import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Rocket, LogIn, LayoutDashboard, CreditCard } from "lucide-react";
import { Button } from "../ui/button";
import { LOGO_URL, NAV_LINKS } from "../../lib/landing-data";
import { useAuthDialog } from "../../lib/auth-dialog-context";
import { useAuth } from "../../lib/auth-context";
import { useSubscription } from "../../lib/subscription-context";

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openAuth } = useAuthDialog();
  const { user } = useAuth();
  const { subscription } = useSubscription();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -40, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/80 backdrop-blur-xl border-b border-purple-900/10 shadow-sm"
          : "bg-white/40 backdrop-blur-md"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <a
            href="#home"
            data-testid="navbar-logo"
            className="flex items-center group"
          >
            <img
              src={LOGO_URL}
              alt="EasyLister · Easy Listings. Sales Growth. Automation."
              className="h-9 sm:h-11 w-auto object-contain transition-transform group-hover:scale-[1.03]"
              draggable="false"
            />
          </a>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-purple-700 hover:bg-purple-50 rounded-full transition-colors"
              >
                {l.label}
              </a>
            ))}
            <Link
              to="/privacy-policy"
              data-testid="nav-link-privacy"
              className="px-4 py-2 text-sm font-medium text-slate-700 hover:text-purple-700 hover:bg-purple-50 rounded-full transition-colors"
            >
              Privacy
            </Link>
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            {user ? (
              <>
                <div
                  data-testid="navbar-plan-pill"
                  className="inline-flex items-center gap-2 px-3 h-9 rounded-full bg-purple-50 border border-purple-200 text-purple-700 text-xs font-bold capitalize"
                >
                  <CreditCard className="w-3.5 h-3.5" />
                  {subscription?.plan ?? "No plan"}
                </div>
                <Button
                  asChild
                  data-testid="navbar-open-panel-button"
                  className="rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold shadow-lg shadow-purple-600/20 px-5"
                >
                  <Link to="/app">
                    <LayoutDashboard className="w-4 h-4 mr-2" /> Open Panel
                  </Link>
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="ghost"
                  onClick={() => openAuth("login")}
                  data-testid="navbar-login-button"
                  className="rounded-full text-slate-700 hover:text-purple-700 hover:bg-purple-50 font-semibold"
                >
                  <LogIn className="w-4 h-4 mr-1.5" /> Log in
                </Button>
                <Button
                  onClick={() => openAuth("signup")}
                  data-testid="navbar-get-started-button"
                  className="rounded-full bg-purple-600 hover:bg-purple-700 text-white font-semibold shadow-lg shadow-purple-600/20 px-5"
                >
                  <Rocket className="w-4 h-4 mr-2" /> Get Started — Free
                </Button>
              </>
            )}
          </div>

          <button
            className="lg:hidden p-2 rounded-lg hover:bg-purple-50 text-slate-700"
            onClick={() => setOpen(!open)}
            aria-label="toggle menu"
            data-testid="mobile-menu-toggle"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              className="lg:hidden overflow-hidden border-t border-purple-100"
            >
              <div className="py-4 flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <a
                    key={l.href}
                    href={l.href}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                    className="px-4 py-3 text-sm font-medium text-slate-700 hover:bg-purple-50 hover:text-purple-700 rounded-xl"
                  >
                    {l.label}
                  </a>
                ))}
                <Link
                  to="/privacy-policy"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-link-privacy"
                  className="px-4 py-3 text-sm font-medium text-slate-700 hover:bg-purple-50 hover:text-purple-700 rounded-xl"
                >
                  Privacy
                </Link>
                {user ? (
                  <Button
                    asChild
                    onClick={() => setOpen(false)}
                    data-testid="mobile-open-panel-button"
                    className="mt-2 rounded-full bg-purple-600 hover:bg-purple-700 text-white"
                  >
                    <Link to="/app">
                      <LayoutDashboard className="w-4 h-4 mr-2" /> Open Panel
                    </Link>
                  </Button>
                ) : (
                  <>
                    <Button
                      variant="outline"
                      onClick={() => {
                        setOpen(false);
                        openAuth("login");
                      }}
                      data-testid="mobile-login-button"
                      className="mt-2 rounded-full border-purple-200 text-purple-700 hover:bg-purple-50"
                    >
                      <LogIn className="w-4 h-4 mr-2" /> Log in
                    </Button>
                    <Button
                      onClick={() => {
                        setOpen(false);
                        openAuth("signup");
                      }}
                      data-testid="mobile-get-started-button"
                      className="rounded-full bg-purple-600 hover:bg-purple-700 text-white"
                    >
                      <Rocket className="w-4 h-4 mr-2" /> Get Started — Free
                    </Button>
                  </>
                )}
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </motion.header>
  );
};
